import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogContent } from '@material-ui/core';

import { addressesApi } from 'api/addresses';
import { procurementApi as api } from 'api/procurement';
import {
    createPaymentPriorityColumn,
    mapVendorPrioritiesToOptions,
    mapVendorsWithPriorities,
} from 'components/vendor/utils/vendorPriorities';
import { DataTable, FormComponent, Notifications } from 'shared';
import { validEmail } from 'shared/utils/validation';

function VendorAddressModal({
    country,
    countryOpts,
    editing,
    getStates,
    getVendors,
    handleProvChange,
    mainCountryCodes,
    row,
    setEditing,
    setNotification,
    stateOpts,
}) {
    useEffect(() => {
        if (country?.text) {
            getStates(country?.text);
        }
    }, []);

    return (
        <Dialog open={editing} onClose={() => setEditing(false)} fullWidth width="400px">
            <DialogContent>
                <FormComponent
                    formFields={[
                        {
                            name: 'email',
                            inputElement: 'textField',
                            label: 'MPOC Email',
                            gridValue: 6,
                            inputProps: { required: true },
                        },
                        {
                            name: 'firstName',
                            inputElement: 'textField',
                            label: 'Rep First Name',
                            gridValue: 3,
                            inputProps: { required: true },
                        },
                        {
                            name: 'lastName',
                            inputElement: 'textField',
                            label: 'Rep Last Name',
                            gridValue: 3,
                            inputProps: { required: true },
                        },
                        {
                            name: 'lineOne',
                            inputElement: 'textField',
                            label: 'Address 1',
                            gridValue: 12,
                            inputProps: { required: true },
                        },
                        {
                            name: 'lineTwo',
                            inputElement: 'textField',
                            label: 'Address 2',
                            gridValue: 12,
                        },
                        {
                            name: 'country',
                            label: 'Country',
                            inputElement: 'autocomplete',
                            gridValue: 3,
                            inputProps: { opts: countryOpts, onChange: handleProvChange },
                        },
                        {
                            name: 'province',
                            label: 'State/Province',
                            inputElement: 'select',
                            gridValue: 3,
                            inputProps: {
                                opts: stateOpts,
                                required: !!mainCountryCodes.includes(country?.value),
                            },
                            initialValue: row.original.province,
                        },
                        {
                            name: 'city',
                            inputElement: 'textField',
                            label: 'City',
                            gridValue: 8,
                            inputProps: { required: true },
                        },
                        {
                            name: 'zip',
                            inputElement: 'textField',
                            label: 'Zip Code',
                            inputProps: { maxLength: 5, required: true },
                            gridValue: 2,
                        },
                    ]}
                    onSubmit={async (formData) => {
                        const sendParams = {
                            email: formData?.email,
                            firstName: formData?.firstName,
                            lastName: formData?.lastName,
                            address1: formData?.lineOne,
                            address2: formData?.lineTwo,
                            country: formData?.country,
                            province: mainCountryCodes.includes(formData?.country) ? formData?.province : 'N/A',
                            city: formData?.city,
                            zip: formData?.zip,
                        };

                        const response = await api.updateVendorAddress(row.original.id, sendParams);
                        if (response.status === false) {
                            let message = 'Error modifying vendor address';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setNotification({ text: message, level: 'error' });
                        } else {
                            setNotification({ text: 'Vendor address modified!', level: 'success' });
                            getVendors();
                        }
                        setEditing(false);
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}

function AddressCell({ countryOpts, getVendors, row, setNotification }) {
    const [editing, setEditing] = useState(false);
    const [stateOpts, setStateOpts] = useState([]);
    const [country, setCountry] = useState(countryOpts.find((c) => c.value === row.original.country));
    const mainCountryCodes = ['US', 'MX', 'CA'];

    // Load state options
    const getStates = async (country) => {
        if (country == 'US' || country === 'United States') {
            country = 'unitedstates';
        }

        if (country === undefined) {
            const pleaseSelect = { text: 'Please Select Country', value: 0 };
            setStateOpts([pleaseSelect]);
            return;
        }

        const response = await api.getProvinces(country);
        if (response.status === false) {
            setNotification({ text: `No provinces found ${response.msg}`, level: 'error' });
            const pleaseSelect = { text: 'Please Select Country', value: 0 };
            setStateOpts([pleaseSelect]);
            return;
        }

        const stateOpts = response.data.rows.map((row) => {
            const out = {};
            out.text = row.name;
            out.value = row.twoLetter;
            return out;
        });

        const pleaseSelect = { text: 'Please Select', value: 0 };
        setStateOpts([pleaseSelect].concat(stateOpts));
    };

    const handleProvChange = async (e, newValue) => {
        e.preventDefault();
        if (newValue && newValue.value) {
            setCountry(newValue);
            if (mainCountryCodes.includes(newValue.value)) {
                getStates(newValue.text);
            } else {
                setStateOpts([]);
            }
        }
    };

    return (
        <div style={{ textAlign: 'center' }}>
            <div>
                <Button id={`Address_Edit_${row.id}`} onClick={() => setEditing(true)}>
                    Edit
                </Button>
            </div>
            {editing && (
                <VendorAddressModal
                    editing={editing}
                    setEditing={setEditing}
                    row={row}
                    stateOpts={stateOpts}
                    country={country}
                    countryOpts={countryOpts}
                    handleProvChange={handleProvChange}
                    setNotification={setNotification}
                    getVendors={getVendors}
                    mainCountryCodes={mainCountryCodes}
                    getStates={getStates}
                />
            )}
        </div>
    );
}

function IncompleteVendor() {
    const [rows, setRows] = useState([]);
    const [countryOpts, setCountryOpts] = useState([]);
    const [vendorPriorityOpts, setVendorPriorityOpts] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });

    // Load vendors
    const getVendors = async () => {
        const response = await api.getIncompleteVendors();
        if (response.status === false) {
            setNotification({ text: `No vendors found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }
        setRows(mapVendorsWithPriorities(response.data.rows));
    };

    // Load country options
    const getCountries = async () => {
        const response = await addressesApi.getCountries();
        if (response.status === false) {
            setNotification({ text: `No countries found ${response.msg}`, level: 'error' });
            setCountryOpts([]);
            return;
        }

        const countryAsOpts = response.data.rows.map((row) => {
            const out = {};
            out.text = row.name;
            out.value = row.countryCode;
            return out;
        });

        const pleaseSelect = { text: 'Please Select', value: 0 };
        setCountryOpts([pleaseSelect].concat(countryAsOpts));
    };

    const getVendorPriorityOptions = async () => {
        const response = await api.getVendorPriorities();
        if (response.status === false) {
            setNotification({ text: `No payment priorities found ${response.msg}`, level: 'error' });
            setVendorPriorityOpts([]);
            return;
        }
        setVendorPriorityOpts(mapVendorPrioritiesToOptions(response.data.vendorPriorities));
    };

    useEffect(() => {
        getVendors();
        getCountries();
        getVendorPriorityOptions();
    }, []);

    const moqForOpts = [
        { text: 'Delivery', value: 'delivery' },
        { text: 'Pickup', value: 'pickup' },
    ];

    const uomOpts = [
        { text: 'lb', value: 'lb' },
        { text: 'pallet', value: 'pallet' },
        { text: 'case', value: 'case' },
    ];

    // Must match ecommerce VendorPaymentTypeEnum string values.
    const paymentTypeOpts = [
        { text: 'Payment Plan', value: 'Payment Plan' },
        { text: 'Pre Pay', value: 'Pre Pay' },
        { text: 'Pay Upon Receipt', value: 'Pay Upon Receipt' },
    ];

    const columns = [
        { accessor: 'id', Header: 'Vendor ID' },
        {
            Header: 'Address Fields',
            width: '150px',
            Cell: ({ row }) => AddressCell({ row, countryOpts, getVendors, setNotification }),
        },
        { accessor: 'name', Header: 'Name', editable: false },
        { accessor: 'email', Header: 'MPOC Email', editable: false },
        { accessor: 'email1', Header: 'Email 1', editable: true },
        { accessor: 'email2', Header: 'Email 2', editable: true },
        { accessor: 'email3', Header: 'Email 3', editable: true },
        { accessor: 'email4', Header: 'Email 4', editable: true },
        { accessor: 'office', Header: 'Office', editable: true },
        { accessor: 'cell', Header: 'Cell', editable: true },
        { accessor: 'leadTime', Header: 'Lead Time', editable: true },
        { accessor: 'lineOne', Header: 'Line One', editable: false },
        { accessor: 'lineTwo', Header: 'Line Two', editable: false },
        { accessor: 'city', Header: 'City', editable: false },
        { accessor: 'paymentTerms', Header: 'Payment Terms', editable: true },
        {
            accessor: 'paymentType',
            Header: 'Payment Type',
            editable: true,
            editProps: {
                type: 'select',
                options: paymentTypeOpts,
            },
        },
        {
            accessor: 'sharedVendor',
            Header: 'Shared Vendor',
            editable: true,
            type: 'checkbox',
        },
        createPaymentPriorityColumn({
            options: vendorPriorityOpts,
            onRefresh: getVendors,
            setNotification,
            api,
        }),
        { accessor: 'internalLogisticsEmail', Header: 'Internal Logistics Email', editable: true },
        { accessor: 'zip', Header: 'Zip', editable: false },
        {
            accessor: 'moq1For',
            Header: 'MOQ 1 For',
            editable: true,
            editProps: {
                type: 'select',
                options: moqForOpts,
            },
        },
        {
            accessor: 'moq1Uom',
            Header: 'MOQ 1 UOM',
            editable: true,
            editProps: {
                type: 'select',
                options: uomOpts,
            },
        },
        { accessor: 'moq1Number', Header: 'MOQ 1 #', editable: true },
        {
            accessor: 'moq2For',
            Header: 'MOQ 2 For',
            editable: true,
            editProps: { type: 'select', options: moqForOpts },
        },
        { accessor: 'moq2Uom', Header: 'MOQ 2 UOM', editable: true, editProps: { type: 'select', options: uomOpts } },
        { accessor: 'moq2Number', Header: 'MOQ 2 #', editable: true },
    ];

    return (
        <div>
            <h1>Incomplete Vendors</h1>
            <Notifications options={notification} />
            {}
            <div>
                <DataTable
                    columns={columns}
                    data={rows}
                    editable
                    saveEdit={async ({ field, id, value }) => {
                        if (field.startsWith('email') && !validEmail(value)) {
                            setNotification({ text: 'Invalid email address', level: 'error' });
                            return;
                        }

                        const update = {};
                        update[field] = value;

                        const response = await api.updateVendor(id, update);
                        if (response.status === false) {
                            let message = 'Error modifying vendor';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setNotification({ text: message, level: 'error' });
                        } else {
                            setNotification({ text: 'Vendor modified!', level: 'success' });
                            getVendors();
                        }
                    }}
                />
            </div>
        </div>
    );
}

export { IncompleteVendor, AddressCell, VendorAddressModal };
